import {
  createContext,
  useContext,
  type FC,
  type PropsWithChildren,
  useMemo,
} from 'react';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';

import type { EmployerSiteNames as EmployerSiteName } from 'src/siteConfig';

type NumberString = `${number}`;
type NonEmptyNumberStringArray = [NumberString, ...NumberString[]];

interface Experiment<T> {
  name: T;
  experimentGroupRangeConfig?: {
    [K in EmployerSiteName]?: NonEmptyNumberStringArray;
  };
}

interface ProviderState<T> {
  experimentsEnabledByQueryParams: Array<Experiment<T>>;
  supportedExperiments: Array<Experiment<T>>;
  advertiserSeekId: string | null;
  hashedAdvertiserSeekId: string | null;
  site: EmployerSiteName | null;
}

export const createExperimentContext = <T extends string>(
  supportedExperiments: Array<Experiment<T>>,
) => {
  const createDefaultExperimentState = (): ProviderState<T> => ({
    experimentsEnabledByQueryParams: [],
    supportedExperiments,
    advertiserSeekId: null,
    hashedAdvertiserSeekId: null,
    site: null,
  });

  const ExperimentContext = createContext<ProviderState<T>>(
    createDefaultExperimentState(),
  );

  // The hash algorithm is djb2 is a simple, efficient hash algorithm developed by Daniel J. Bernstein.
  // It starts with 5381, then for each character, multiplies the hash by 33 and adds its ASCII value.
  // This generates a unique, well-distributed hash returned as 32-bit unsigned integer.
  function hashNumber(num: string | null): string | null {
    if (!num) {
      return num;
    }

    let hash = 5381;
    for (let i = 0; i < num.length; i++) {
      hash = (hash * 33) ^ num.charCodeAt(i);
    }
    return (hash >>> 0).toString();
  }

  const useExperimentConfig = () => {
    const context = useContext(ExperimentContext);

    if (!context) {
      throw new Error(
        'useFeatureConfig must be used within a FeatureConfigProvider',
      );
    }

    const isExperimentEnabled = (experimentName: T): boolean => {
      if (context.site === null || context.advertiserSeekId === null) {
        return false;
      }

      if (
        context.experimentsEnabledByQueryParams.some(
          (supportedExperiment) => supportedExperiment.name === experimentName,
        )
      ) {
        return true;
      }

      return (
        context.supportedExperiments
          .find(
            (supportedExperiment) =>
              supportedExperiment.name === experimentName,
          )
          ?.experimentGroupRangeConfig?.[context.site]?.some((digit) =>
            context.hashedAdvertiserSeekId?.endsWith(digit),
          ) ?? false
      );
    };

    const getHashedAdvertiserSeekId = (): string | null =>
      context.hashedAdvertiserSeekId;

    return { isExperimentEnabled, getHashedAdvertiserSeekId };
  };

  const ExperimentConfigProvider: FC<
    PropsWithChildren<{
      advertiserSeekId: string | null;
      site: EmployerSiteName;
    }>
  > = ({ children, advertiserSeekId, site }) => {
    const [experimentsQueryParam] = useQueryParam(
      'experiments',
      withDefault(StringParam, ''),
    );

    const featuresArray = experimentsQueryParam.split(',');
    const experimentsEnabledByQueryParams = supportedExperiments.filter(
      ({ name }) => featuresArray.includes(name),
    );

    const providerState = useMemo(
      () => ({
        supportedExperiments,
        advertiserSeekId,
        hashedAdvertiserSeekId: hashNumber(advertiserSeekId),
        experimentsEnabledByQueryParams,
        site,
      }),
      [advertiserSeekId, experimentsEnabledByQueryParams, site],
    );

    return (
      <ExperimentContext.Provider value={providerState}>
        {children}
      </ExperimentContext.Provider>
    );
  };

  return {
    useExperimentConfig,
    ExperimentConfigProvider,
  };
};
